// Sinh món đồ (ITEMS §3.4–3.5, §5.1, §5.5, §7.5). Hàm thuần, mọi ngẫu nhiên qua Rng có seed.
import { BALANCE } from '@data/balance';
import { affixDef, affixPool } from '@data/items/affixes';
import { basesFor, baseDef } from '@data/items/bases';
import { DAO_GLYPHS } from '@data/items/glyphs';
import { uniqueDef } from '@data/items/uniques';
import {
  RARITIES,
  type AffixDef,
  type AffixTier,
  type ItemInstance,
  type ItemLine,
  type Rarity,
  type ScaleLetter,
  type SlotKind,
  type TriKey,
  type WeaponType,
} from '@data/items/types';
import type { Rng } from '../core/rng';

/** Số dòng theo phẩm (ITEMS §3.4): 1/1/2/2/3/3/4. */
export const RARITY_LINES: Readonly<Record<Rarity, number>> = {
  mortal: 1,
  yellow: 1,
  mystic: 2,
  earth: 2,
  heaven: 3,
  immortal: 3,
  chaos: 4,
};

export type LootSource = keyof typeof BALANCE.AFFIX_TIER_WEIGHTS;

export function rarityIndex(r: Rarity): number {
  return RARITIES.indexOf(r);
}

function pickWeighted<T>(rng: Rng, items: readonly T[], weight: (x: T) => number): T {
  let total = 0;
  for (const x of items) total += weight(x);
  let roll = rng.next() * total;
  for (const x of items) {
    roll -= weight(x);
    if (roll < 0) return x;
  }
  return items[items.length - 1]!;
}

/** Roll bậc rồi nấc (20 nấc đều trong khoảng của bậc). Cực = nấc cuối của T1. */
export function rollLine(def: AffixDef, source: LootSource, rng: Rng): ItemLine {
  const w = BALANCE.AFFIX_TIER_WEIGHTS[source];
  // w viết T5 → T1; bậc 5..1.
  const tiers: AffixTier[] = [5, 4, 3, 2, 1];
  const tier = pickWeighted(rng, tiers, (t) => w[5 - t]!);
  const n = BALANCE.AFFIX_ROLL_NOTCHES;
  const notch = rng.int(n);
  const [lo, hi] = def.ranges[tier - 1]!;
  return { affix: def.id, tier, notch, value: lo + ((hi - lo) * notch) / (n - 1) };
}

/**
 * Tam Nguyên trội của vũ khí: nhánh có hệ số scaling cao nhất, chỉ khi cao nhất duy nhất
 * (vũ khí cân như B/B/C thì không nhánh nào trội). Loot cá nhân ưu tiên dòng khớp (ITEMS §7.5).
 */
export function dominantTri(scaling?: Readonly<Record<TriKey, ScaleLetter>>): TriKey | undefined {
  if (!scaling) return undefined;
  const coef = BALANCE.WEAPON_SCALE_COEF as Record<string, number>;
  const keys = Object.keys(scaling) as TriKey[];
  const top = Math.max(...keys.map((k) => coef[scaling[k]] ?? 0));
  const best = keys.filter((k) => (coef[scaling[k]] ?? 0) === top);
  return best.length === 1 ? best[0] : undefined;
}

export function rollLines(
  slot: SlotKind,
  count: number,
  source: LootSource,
  rng: Rng,
  exclude: string[] = [],
  favorTri?: TriKey,
): ItemLine[] {
  const pool = affixPool(slot).filter((d) => !exclude.includes(d.id));
  const lines: ItemLine[] = [];
  const used = new Set<string>();
  for (let i = 0; i < count && used.size < pool.length; i++) {
    const def = pickWeighted(
      rng,
      pool.filter((d) => !used.has(d.id)),
      (d) =>
        (d.roleSlots?.includes(slot) ? BALANCE.AFFIX_ROLE_WEIGHT : 1) *
        (favorTri && d.id === `tri.${favorTri}` ? BALANCE.LOOT_AFFIX_TRI_MULT : 1),
    );
    used.add(def.id);
    lines.push(rollLine(def, source, rng));
  }
  return lines;
}

function rollGlyph(rng: Rng): { id: string; level: number } {
  const g = DAO_GLYPHS.filter((x) => x.phase === 'P0');
  const id = g[rng.int(g.length)]!.id;
  return { id, level: rng.next() < BALANCE.GLYPH_DROP_LEVEL2_CHANCE ? 2 : 1 };
}

export interface RollItemInput {
  uid: string;
  slot: SlotKind;
  rarity: Rarity;
  giai: number;
  source: LootSource;
  /** Loại vũ khí đang dùng: 60% rơi đúng loại (ITEMS §7.5). */
  weaponPref?: WeaponType;
  /** Tam Nguyên trội của vũ khí đang dùng: dòng khớp ×1,5 (ITEMS §7.5). */
  favorTri?: TriKey;
  origin: string;
  atMs: number;
}

export function rollItem(inp: RollItemInput, rng: Rng): ItemInstance {
  let bases = basesFor(inp.slot);
  if (inp.slot === 'weapon' && inp.weaponPref) {
    const same = bases.filter((b) => b.weaponType === inp.weaponPref);
    const other = bases.filter((b) => b.weaponType !== inp.weaponPref);
    if (same.length && other.length) bases = rng.next() < BALANCE.LOOT_WEAPON_SAME_TYPE ? same : other;
  }
  const base = bases[rng.int(bases.length)]!;
  const item: ItemInstance = {
    uid: inp.uid,
    base: base.id,
    rarity: inp.rarity,
    giai: inp.giai,
    lines: rollLines(inp.slot, RARITY_LINES[inp.rarity], inp.source, rng, [], inp.favorTri),
    enhance: 0,
    enhanceFailStacks: 0,
    source: inp.origin,
    gotAtMs: inp.atMs,
  };
  if (rarityIndex(inp.rarity) >= rarityIndex('heaven')) item.daoGlyph = rollGlyph(rng);
  return item;
}

/** Độc bản (ITEMS §5.5): dòng cố định roll bậc theo nguồn boss, đủ số dòng Tiên thì thêm dòng ngẫu nhiên. */
export function rollUnique(
  uniqueId: string,
  giai: number,
  uid: string,
  atMs: number,
  rng: Rng,
): ItemInstance {
  const u = uniqueDef(uniqueId);
  const slot = baseDef(u.base).slot;
  const lines = u.fixedAffixes.map((id) => rollLine(affixDef(id), 'boss', rng));
  const extra = RARITY_LINES[u.rarity] - lines.length;
  if (extra > 0) lines.push(...rollLines(slot, extra, 'boss', rng, [...u.fixedAffixes]));
  return {
    uid,
    base: u.base,
    rarity: u.rarity,
    giai,
    lines,
    daoGlyph: rollGlyph(rng),
    uniqueId,
    enhance: 0,
    enhanceFailStacks: 0,
    source: u.source,
    gotAtMs: atMs,
  };
}

/** Phẩm đồ nền của Lịch Luyện: Phàm 70 · Hoàng 25 · Huyền 5; Phúc Duyên nhân các phẩm trên Phàm. */
export function rollIdleRarity(rng: Rng, itemFindPct: number): Rarity {
  const w = BALANCE.IDLE_EQUIP_RARITY;
  const k = 1 + Math.max(0, itemFindPct) / 100;
  const opts: [Rarity, number][] = [
    ['mortal', w.mortal],
    ['yellow', w.yellow * k],
    ['mystic', w.mystic * k],
  ];
  return pickWeighted(rng, opts, (o) => o[1])[0];
}

export { pickWeighted };
